import {
  createStyles,
  TextField,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { Search } from "@mui/icons-material";
import { useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import { Show } from "features/shared/components";
import SkeletonLoading from "features/shared/components/SkeletonLoading";
import React from "react";
import type { UserManagementFilter } from "../../../userManagement.types";
import { useFilter } from "../hooks/useFilter";

type UserListFilterProps = {
  clientId: number | null;
  filter: UserManagementFilter;
  onFilterChange: (filter: UserManagementFilter) => void;
} & WithStyles<typeof styles>;

const UserListFilter = ({
  clientId,
  filter,
  onFilterChange,
  classes,
}: UserListFilterProps) => {
  const { t } = useTranslations();

  const { search, status, farms, retreat, permissions } = useFilter({
    clientId,
    filter,
    onFilterChange,
  });

  return (
    <div className={classes.root}>
      <TextField
        data-testid="user-list-search-input"
        classes={{ root: classes.searchInput }}
        variant="standard"
        InputProps={{
          startAdornment: <Search className={classes.searchIcon} />,
        }}
        label={t("common:actions.search").toUpperCase()}
        InputLabelProps={{ shrink: true }}
        placeholder={t("user:search-users-placeholder")}
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
      />
      <SelectDropdown
        testId="user-list-status-dropdown"
        styles={{ width: "auto", minWidth: 129, maxWidth: 300 }}
        label={{ text: t("common:status.status").toUpperCase() }}
        options={status.options}
        value={status.selected}
        onChange={status.onChange}
      />
      <Show when={!farms.isLoading} fallback={<MultiSelectSkeleton />}>
        <SelectDropdown
          isMulti
          testId="user-list-farms-dropdown"
          styles={{ width: "auto", minWidth: 227, maxWidth: 300 }}
          label={{ text: t("locales:farms").toUpperCase() }}
          options={farms.options}
          value={farms.selected}
          onChange={farms.onChange}
          isLoading={farms.isLoading}
        />
      </Show>
      <Show when={!retreat.isLoading} fallback={<MultiSelectSkeleton />}>
        <SelectDropdown
          isMulti
          testId="user-list-retreats-dropdown"
          styles={{ width: "auto", minWidth: 170, maxWidth: 300 }}
          label={{ text: t("locales:retreats").toUpperCase() }}
          options={retreat.options}
          value={retreat.selected}
          onChange={retreat.onChange}
          isLoading={retreat.isLoading}
        />
      </Show>
      <SelectDropdown
        isMulti
        testId="user-list-permissions-dropdown"
        styles={{ width: "auto", minWidth: 200, maxWidth: 300 }}
        label={{ text: t("common:permissions").toUpperCase() }}
        options={permissions.options}
        value={permissions.selected}
        onChange={permissions.onChange}
      />
    </div>
  );
};

const MultiSelectSkeleton = () => <SkeletonLoading width={250} height={56} />;

const styles = () =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: "24px",
    },
    searchInput: {
      minWidth: "250px !important",
    },
    searchIcon: {
      color: "#616161",
    },
  });

export default withStyles(styles)(UserListFilter);
